import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Mail, Phone, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { useLanguage, waLink } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const { t } = useLanguage();
  const [nlEmail, setNlEmail] = useState("");
  const [nlDone, setNlDone] = useState(false);

  const subscribe = async () => {
    const email = nlEmail.trim();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error(t("nl_error"));
      return;
    }
    try {
      const r = await fetch(`${API}/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!r.ok) throw new Error();
      setNlDone(true);
      toast.success(t("nl_success"));
    } catch {
      toast.error(t("nl_error"));
    }
  };

  const legalLinks = [
    { to: "/legal/privacy", label: t("privacy"), id: "footer-privacy" },
    { to: "/legal/cookie", label: t("cookie"), id: "footer-cookie" },
    { to: "/legal/terms", label: t("terms"), id: "footer-terms" },
    { to: "/legal/booking-info", label: t("booking_info"), id: "footer-booking-info" },
    { to: "/legal/cancellation", label: t("cancellation_policy"), id: "footer-cancellation" },
    { to: "/faq", label: t("faq"), id: "footer-faq" },
  ];

  return (
    <footer className="bg-ink text-ivory" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 md:grid-cols-4 gap-12">
        <div className="md:col-span-1">
          <div className="flex flex-col leading-none mb-4">
            <span className="font-serif text-3xl tracking-tight">Flegrea</span>
            <span className="text-[10px] font-medium uppercase tracking-[0.35em] text-terracotta">Experience</span>
          </div>
          <p className="text-sm text-ivory/60 leading-relaxed">{t("tagline")}</p>
          <div className="flex gap-4 mt-6">
            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" data-testid="social-instagram" aria-label="Instagram" className="text-ivory/50 hover:text-terracotta transition-colors duration-300">
              <Instagram size={18} strokeWidth={1.5} />
            </a>
            <a href="https://tiktok.com" target="_blank" rel="noopener noreferrer" data-testid="social-tiktok" aria-label="TikTok" className="text-ivory/50 hover:text-terracotta transition-colors duration-300 text-xs font-semibold tracking-wide flex items-center">
              TikTok
            </a>
            <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" data-testid="social-facebook" aria-label="Facebook" className="text-ivory/50 hover:text-terracotta transition-colors duration-300">
              <Facebook size={18} strokeWidth={1.5} />
            </a>
          </div>
        </div>

        <div>
          <h4 className="text-[11px] uppercase tracking-[0.25em] text-ivory/40 mb-5">Menu</h4>
          <ul className="space-y-3 text-sm">
            <li><Link to="/experiences" data-testid="footer-experiences" className="text-ivory/70 hover:text-ivory transition-colors duration-300">Experiences</Link></li>
            <li><Link to="/boat" data-testid="footer-boat" className="text-ivory/70 hover:text-ivory transition-colors duration-300">{t("nav_boat")}</Link></li>
            <li><Link to="/taste" data-testid="footer-taste" className="text-ivory/70 hover:text-ivory transition-colors duration-300">Taste</Link></li>
            <li><Link to="/experiences?category=nature" data-testid="footer-nature" className="text-ivory/70 hover:text-ivory transition-colors duration-300">{t("nature_word")}</Link></li>
            <li><Link to="/stay" data-testid="footer-stay" className="text-ivory/70 hover:text-ivory transition-colors duration-300">Stay</Link></li>
            <li><Link to="/create" data-testid="footer-create" className="text-ivory/70 hover:text-ivory transition-colors duration-300">Create Your Experience</Link></li>
            <li><Link to="/contact" data-testid="footer-contact" className="text-ivory/70 hover:text-ivory transition-colors duration-300">{t("contact")}</Link></li>
            <li><Link to="/companion" data-testid="footer-companion" className="text-ivory/70 hover:text-ivory transition-colors duration-300">Companion</Link></li>
            <li><Link to="/gallery" data-testid="footer-gallery" className="text-ivory/70 hover:text-ivory transition-colors duration-300">Gallery</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-[11px] uppercase tracking-[0.25em] text-ivory/40 mb-5">{t("footer_contacts")}</h4>
          <ul className="space-y-3 text-sm">
            <li>
              <a href="mailto:info@flegreaexperience.com" data-testid="footer-email" className="flex items-center gap-2 text-ivory/70 hover:text-ivory transition-colors duration-300">
                <Mail size={14} strokeWidth={1.5} /> info@flegreaexperience.com
              </a>
            </li>
            <li>
              <a href="tel:+393793346030" data-testid="footer-phone" className="flex items-center gap-2 text-ivory/70 hover:text-ivory transition-colors duration-300">
                <Phone size={14} strokeWidth={1.5} /> +39 379 334 6030
              </a>
            </li>
            <li>
              <a href={waLink(t("wa_message") + " ")} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" className="flex items-center gap-2 text-ivory/70 hover:text-ivory transition-colors duration-300">
                <MessageCircle size={14} strokeWidth={1.5} /> WhatsApp
              </a>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="text-[11px] uppercase tracking-[0.25em] text-ivory/40 mb-5">{t("nl_title")}</h4>
          <p className="text-sm text-ivory/60 leading-relaxed mb-5">{t("nl_sub")}</p>
          {nlDone ? (
            <p className="text-sm text-sage leading-relaxed" data-testid="newsletter-success">{t("nl_success")}</p>
          ) : (
            <div className="flex gap-2" data-testid="newsletter-form">
              <input
                type="email"
                value={nlEmail}
                onChange={(e) => setNlEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") subscribe(); }}
                placeholder={t("nl_placeholder")}
                data-testid="newsletter-email-input"
                className="flex-1 min-w-0 bg-transparent border border-ivory/25 px-4 py-3 text-sm text-ivory placeholder:text-ivory/35 focus:outline-none focus:border-terracotta transition-colors"
              />
              <button
                type="button"
                onClick={subscribe}
                data-testid="newsletter-submit-button"
                aria-label={t("nl_cta")}
                className="bg-terracotta text-ink px-4 py-3 text-xs font-medium uppercase tracking-[0.15em] hover:bg-ivory transition-colors duration-300 inline-flex items-center gap-2"
              >
                <Send size={13} strokeWidth={1.5} />
                <span className="hidden sm:inline">{t("nl_cta")}</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-ivory/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs" data-testid="footer-legal-bar">
            {legalLinks.map((l) => (
              <Link key={l.id} to={l.to} data-testid={l.id} className="text-ivory/50 hover:text-ivory transition-colors duration-300">
                {l.label}
              </Link>
            ))}
            <button
              data-testid="footer-manage-cookies"
              onClick={() => window.dispatchEvent(new Event("fx:open-cookie-preferences"))}
              className="text-ivory/50 hover:text-ivory transition-colors duration-300"
            >
              {t("manage_cookies")}
            </button>
          </div>
          <div className="flex flex-col sm:flex-row justify-between gap-2 text-xs text-ivory/40">
            <span data-testid="footer-copy">© {new Date().getFullYear()} Flegrea Experience</span>
            <span>From Volcanoes to Mountains. From Ancient Cities to the Mediterranean Sea.</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
